import { Box, Typography, Card, Stack } from "@mui/material";
import { Bar } from "react-chartjs-2";
import TooltipLight from "utils/TooltipLight";
import { useEffect, useRef, useState } from "react";
import badgeImageMap from "utils/Badges/badgeImageMap";

const Badge = ({ badge, userHasBadge, graphData, dateDisplay }) => {
  const [showDateDisplay, setShowDateDisplay] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setShowDateDisplay(width > 190);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const data = {
    labels: [""],
    datasets: [
      {
        label: "count",
        // backgroundColor: "cornflowerblue",
        data: [graphData.value],
        borderRadius: 10,
        borderSkipped: false,
        backgroundColor: (context) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return "cornflowerblue";
          }

          // Create gradient
          const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
          if (badge.criteria_type === "streak" || badge.criteria_type === "streak_all_time") {
            gradient.addColorStop(0, "#ff7b11");
            gradient.addColorStop(1, "#ffc438");
          } else if (badge.criteria_type === "revenue") {
            gradient.addColorStop(0, "#05aa2f");
            gradient.addColorStop(1, "#96ff41");
          } else {
            gradient.addColorStop(0, "#6060fa");
            gradient.addColorStop(1, "#6bc5fa");
          }

          return gradient;
        },
      },
      {
        label: "target",
        backgroundColor: "#666",
        hoverBackgroundColor: "#666",
        data: [graphData.max],
        borderRadius: 10,
        borderSkipped: false,
      },
    ],
  };
  const options = {
    maintainAspectRatio: false,
    responsive: true,
    indexAxis: "y",
    animation: false,
    scales: {
      x: {
        display: false,
        stacked: false,
        min: 0,
        max: graphData.max,
      },
      y: {
        display: false,
        stacked: true,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    hover: {
      mode: null,
      animationDuration: 0,
    },
  };

  const formatUSD = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatGraphValue = () => {
    const { value, max } = graphData;
    if (badge.criteria_type === "revenue") {
      return value < max ? formatUSD(value) : "";
    }
    if (badge.criteria_type === "streak" && badge.FK_badge_scopes === "team") {
      if (value < max) {
        return `${value} ${value === 1 ? "member" : "members"}`;
      }
      return "";
    }

    return value < max ? value : "";
  };

  const formatGraphMax = () => {
    const { max } = graphData;
    if (badge.criteria_type === "revenue") {
      return formatUSD(max);
    }
    if (badge.criteria_type === "streak" && badge.FK_badge_scopes === "team") {
      return max === 1 ? `${max} member` : `${max} members`;
    }
    return max;
  };

  return (
    <Box
      ref={containerRef}
      key={badge.id}
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <TooltipLight title={badge.description} variant="outline">
        <Card
          sx={{
            width: "100%",
            height: "300px",
            p: "0.5rem 1rem 1rem 1rem",
            boxShadow: "none",
            backgroundColor: "#323236",
          }}
        >
          <Stack height="100%" justifyContent="space-between">
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  filter: userHasBadge ? "none" : "grayscale(50%)",
                }}
              >
                <img
                  src={badge?.badge_image && badgeImageMap[badge.badge_image]}
                  alt={badge?.badge_image}
                  loading="lazy"
                  style={{
                    filter: "drop-shadow(4px 4px 6px rgba(0, 0, 0, 0.3))",
                    opacity: userHasBadge ? 1 : 0.3,
                    maxHeight: "175px",
                  }}
                />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mt: "-0.5rem",
                  fontSize: "0.9rem",
                  fontWeight: "700",
                  lineHeight: "1.15rem",
                  color: userHasBadge ? "#ddd9d5" : "#6b6c6f",
                  letterSpacing: "0.5px",
                }}
              >
                {badge.name}
              </Typography>
            </Box>
            <Stack sx={{ textAlign: "center" }}>
              {graphData && graphData.max > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontStyle: "italic", color: "#888", fontSize: "0.9rem" }}>
                    {formatGraphValue()}
                  </Typography>
                  <Typography sx={{ fontStyle: "italic", color: "#888", fontSize: "0.9rem" }}>
                    {formatGraphMax()}
                  </Typography>
                </Stack>
              )}
              <Box height="8px" width="100%">
                <Bar options={options} data={data} />
              </Box>
              <Box
                sx={{
                  height: "25px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                }}
              >
                {showDateDisplay && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      lineHeight: "1rem",
                      color: "#666",
                    }}
                  >
                    {dateDisplay ? `Awarded on ${dateDisplay}` : null}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Stack>
        </Card>
      </TooltipLight>
    </Box>
  );
};

export default Badge;
